import { AlertOctagon, AlertTriangle, CheckCircle2, Coins } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { cn, formatCurrency } from "@/lib/utils";

interface Props {
  totalAssetValue: number;
  inStockCount: number;
  lowStockCount: number;
  outOfStockCount: number;
  activeStatusFilter: string;
  onSelectStatusFilter: (status: string) => void;
}

export function StockKpiCards({
  totalAssetValue,
  inStockCount,
  lowStockCount,
  outOfStockCount,
  activeStatusFilter,
  onSelectStatusFilter,
}: Props) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
      {/* Total Asset Valuation */}
      <Card className="shadow-xs">
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Total Nilai Aset Stok
          </CardTitle>
          <Coins className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-primary" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold font-mono text-foreground truncate">
            {formatCurrency(totalAssetValue)}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Nilai persediaan barang di toko
          </p>
        </CardContent>
      </Card>

      {/* In Stock */}
      <Card
        onClick={() => onSelectStatusFilter("in_stock")}
        className={cn(
          "shadow-xs cursor-pointer transition-all hover:border-emerald-500/50",
          activeStatusFilter === "in_stock" &&
            "ring-2 ring-emerald-500/50 bg-emerald-500/5",
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Stok Aman
          </CardTitle>
          <CheckCircle2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-emerald-500" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold text-emerald-600 dark:text-emerald-400">
            {inStockCount}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Jumlah barang di atas batas minimum
          </p>
        </CardContent>
      </Card>

      {/* Low Stock Warning */}
      <Card
        onClick={() => onSelectStatusFilter("low_stock")}
        className={cn(
          "shadow-xs cursor-pointer transition-all hover:border-amber-500/50",
          activeStatusFilter === "low_stock" &&
            "ring-2 ring-amber-500/50 bg-amber-500/5",
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Stok Menipis (Restock)
          </CardTitle>
          <AlertTriangle className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-amber-500" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold text-amber-600 dark:text-amber-400">
            {lowStockCount}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Perlu segera dipesan / dibelanja
          </p>
        </CardContent>
      </Card>

      {/* Out of Stock Alert */}
      <Card
        onClick={() => onSelectStatusFilter("out_of_stock")}
        className={cn(
          "shadow-xs cursor-pointer transition-all hover:border-destructive/50",
          activeStatusFilter === "out_of_stock" &&
            "ring-2 ring-destructive/50 bg-destructive/5",
        )}
      >
        <CardHeader className="flex flex-row items-center justify-between p-3 sm:p-6 pb-1 sm:pb-2">
          <CardTitle className="text-xs sm:text-sm font-medium text-muted-foreground line-clamp-1">
            Stok Habis (0)
          </CardTitle>
          <AlertOctagon className="h-3.5 w-3.5 sm:h-4 sm:w-4 shrink-0 text-destructive" />
        </CardHeader>
        <CardContent className="p-3 sm:p-6 pt-0 sm:pt-0">
          <div className="text-lg sm:text-2xl font-bold text-destructive">
            {outOfStockCount}
          </div>
          <p className="mt-0.5 sm:mt-1 text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
            Barang kosong di toko
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
